import React, { useContext } from 'react';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { useNavigate } from 'react-router-dom';
import Links, { LinkItem } from '../../common/Widgets/Links';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { SiteContext } from '../../../context/SiteContext';
import InsuranceDocumentLink from './InsuranceDocumentLink';
import ViewDocument from '../../common/ViewDocument/ViewDocument';

type InsuranceProductProps = {
  name: string;
  description: string;
  description2?: string;
  isIPIDLinkAvailable: boolean;
  policyDocumentName: string;
  ipidDocumentName?: string;
  documentScheme?: string;
  documentType?: string;
};

const InsuranceProduct = ({
  name,
  description,
  description2,
  isIPIDLinkAvailable,
  policyDocumentName,
  ipidDocumentName,
  documentScheme,
  documentType
}: InsuranceProductProps): JSX.Element => {
  const { site } = useContext(SiteContext);
  const { brand } = site;

  return (
    <>
      <div className='flex-row insurance-product'>
        <div className='flex-column'>
          <p className='product-name'>{name}</p>
          <p>{description}</p>
          {description2 && <p>{description2}</p>}
        </div>
        <div>
          <div className='flex-column m05-top'>
            {documentType && documentScheme && (
              <>
                <InsuranceDocumentLink
                  policyDocumentScheme={documentScheme}
                  policyDocumentId={brand.tiaProductCode}
                  policyDocumentType={documentType}
                  displayText='> Policy document'
                  additionalClasses='inherited'
                />
                {isIPIDLinkAvailable && (
                  <InsuranceDocumentLink
                    policyDocumentScheme={documentScheme}
                    policyDocumentId={brand.tiaProductCode}
                    policyDocumentType={documentType}
                    displayText='> IPID statement'
                    additionalClasses='inherited'
                  />
                )}
              </>
            )}

            {!documentType && (
              <>
                <ViewDocument
                  path={`docs/${site.brand.brandName.toLowerCase()}/${policyDocumentName}.pdf`}
                  documentName='> Policy Document'
                />
                {isIPIDLinkAvailable && (
                  <ViewDocument
                    path={`docs/${site.brand.brandName.toLowerCase()}/${ipidDocumentName}.pdf`}
                    documentName='> IPID statement'
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <hr />
    </>
  );
};

const PolicyDocuments = () => {
  const navigate = useNavigate();

  const { site } = useContext(SiteContext);

  const { brand } = site;

  const {
    brandDisplayFullName,
    brandPhoneNumbers,
    useChangesNumberOnPolicyDocsPage
  } = brand;

  const phoneNumber = useChangesNumberOnPolicyDocsPage
    ? brandPhoneNumbers.changesAndComplaintsNumber
    : brandPhoneNumbers.quotesAndRenewalsNumber;

  const goBack = (): void => {
    navigate(-1);
  };

  const linkItems: LinkItem[] = [
    {
      to: '',
      text: 'Back',
      onClick: goBack
    }
  ];

  const chevronLeftComponent = <FontAwesomeIcon icon={faChevronLeft} />;
  return (
    <div className='policy-documents'>
      <Links
        id='policy-documents'
        items={linkItems}
        icon={chevronLeftComponent}
      />
      <h4>Our Address</h4>
      <p>
        For Annual Motor Insurance or Driveaway Insurance correspondence,
        including complaints, please use our new postal address rather than the
        one shown in your policy document:
      </p>
      <p>
        {brandDisplayFullName} Insurance, Car Care Plan Ltd, Second Floor, Aztec
        Centre, Aztec West, Bristol BS32 4TD
      </p>
      <h4>Complimentary cover</h4>
      <hr />
      <InsuranceProduct
        name='Driveaway Insurance'
        description='Policies starting 21/03/24 - present'
        isIPIDLinkAvailable={true}
        policyDocumentName='Driveaway KGM Policy Document 24-03-min'
        ipidDocumentName='Driveaway-IPID-min'
      />
      <h4>Annual Motor Insurance</h4>
      <hr />
      <InsuranceProduct
        name='Ageas Private Car (APC)'
        description='Policies starting 30/11/23 - present'
        isIPIDLinkAvailable={false}
        policyDocumentName='Ageas Private Car (APC)'
        documentScheme={brand.insuranceDocumentContent?.ageasPrivateCar}
        documentType='PP100'
      />
      <InsuranceProduct
        name='Allianz ABC (formerly LV ABC)'
        description='Policies starting 03/05/24 - present'
        isIPIDLinkAvailable={false}
        policyDocumentName='Allianz ABC (formerly LV ABC)'
        documentScheme={brand.insuranceDocumentContent?.allianzABC}
        documentType='PP100'
      />
      <InsuranceProduct
        name='Allianz ABC (formerly LV ABC)'
        description='Policies starting 12/10/23 - 02/05/24'
        isIPIDLinkAvailable={false}
        policyDocumentName='Allianz ABC (formerly LV ABC)'
      />
      <InsuranceProduct
        name='Allianz Highway (formerly LV Highway)'
        description='Policies starting 03/05/24 - present'
        isIPIDLinkAvailable={false}
        policyDocumentName='Allianz Highway (formerly LV Highway)'
        documentScheme={brand.insuranceDocumentContent?.allianzHighway}
        documentType='PP100'
      />
      <InsuranceProduct
        name='Allianz Highway (formerly LV Highway)'
        description='Policies starting 12/10/23 - 02/05/24'
        isIPIDLinkAvailable={false}
        policyDocumentName='Allianz Highway (formerly LV Highway)'
      />
      <InsuranceProduct
        name='Axa'
        description='Policies starting 15/05/24 - present'
        isIPIDLinkAvailable={false}
        policyDocumentName='AXA'
        documentScheme={brand.insuranceDocumentContent?.aXA}
        documentType='PP100'
      />
      <InsuranceProduct
        name='Covea'
        description='Policies starting 28/11/24 - present'
        isIPIDLinkAvailable={false}
        policyDocumentName='Covea'
        documentScheme={brand.insuranceDocumentContent?.covea}
        documentType='PP100'
      />
      <InsuranceProduct
        name='Covea'
        description='Policies starting 21/09/21 - 27/11/24'
        isIPIDLinkAvailable={false}
        policyDocumentName='Covea Previous'
      />
      <h4>Optional products</h4>
      <hr />
      <InsuranceProduct
        name='Key cover'
        description='Policies starting 12/05/23 – present (Fortegra)'
        isIPIDLinkAvailable={true}
        policyDocumentName='Key Insurance Policy Document 23-05-min'
        ipidDocumentName='Key cover IPID'
      />
      <InsuranceProduct
        name='Courtesy Car Plus 28 Days'
        description='Policies starting 12/05/23 - present (HDI Global Speciality SE)'
        isIPIDLinkAvailable={true}
        policyDocumentName='Courtesy Car Plus Policy Document 23-05'
        ipidDocumentName='Courtesy Car Plus IPID 23-01'
      />
      <InsuranceProduct
        name='Motor Legal Protection'
        description='Policies starting 01/01/23 - present (AmTrust Europe Ltd)'
        isIPIDLinkAvailable={true}
        policyDocumentName='Motor Legal Policy Document 23-01-min'
        ipidDocumentName='MLP IPID'
      />
      <InsuranceProduct
        name='Excess Return Policy'
        description='Policies starting 12/05/23 – present (Fortegra)'
        description2='NB These documents relate to purchased Excess Return policies, not the free Excess Return Voucher'
        isIPIDLinkAvailable={true}
        policyDocumentName='Excess Return'
        ipidDocumentName='Excess Return Policy IPID 23-05-min'
        documentScheme={brand.insuranceDocumentContent?.excessReturnPolicy}
        documentType='PP100'
      />
      <InsuranceProduct
        name='Breakdown Cover (provided by Allianz Global Assistance)'
        description='Policies starting 20/10/22 - present'
        isIPIDLinkAvailable={true}
        policyDocumentName='Breakdown Policy Document 24-03'
        ipidDocumentName='Breakdown IPID 24-03'
      />
      <h4>
        If your policy document is not shown above and you would like a copy,
        please call{' '}
        <a className='calltext' href={`tel:${phoneNumber}`}>
          {phoneNumber}
        </a>
      </h4>
      <div className='flex-row'>
        <ButtonBack
          name='btnBack'
          text='Back'
          onClick={goBack}
          additionalClassNames='btn-back'
        />
      </div>
    </div>
  );
};

export default PolicyDocuments;
